import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { t } from "@lingui/core/macro";

import { api, ApiResponse } from "_/utils";
import { Uuid } from "_/types";
import { useAddToast } from "_/components/toasts";
import { User } from "_/data/users";
import { Project, Revision } from "_/data/projects";

const SCOPE = "printQueue";

export const KEYS = {
  entries: () => [SCOPE, "entries"],
  removedEntries: () => [SCOPE, "entries", "removed"],
};

export type PrintQueueStatus = "queued" | "removed";

export type UserFeedback = {
  score: number;
};

export type AttemptStatus =
  | "complete"
  | "cancelled"
  | "failed"
  | "printing"
  | "paused";

export type PrintQueueAttempt = {
  id: Uuid;
  machineId: Uuid;
  revisionId: Uuid;
  operatorId: Uuid | null;
  fileName: string;
  startedAt: string;
  endedAt: string | null;
  status: AttemptStatus;
  userFeedback?: UserFeedback | null;
};

export type PrintQueueEntry = {
  id: Uuid;
  machineId: Uuid | null;
  revisionId: Uuid;
  submittedBy: Uuid;
  rank: string;
  status: PrintQueueStatus;
  createdAt: string;
  updatedAt: string;
  attempts: PrintQueueAttempt[];
  // Optional enriched fields (populated when using include parameter)
  submittedByUser?: User;
  revision?: Revision;
  project?: Project;
};

export function usePrintQueueEntries({
  include = [],
}: {
  include?: ("users" | "projects" | "revisions")[];
} = {}) {
  async function getPrintQueueEntries(): Promise<PrintQueueEntry[]> {
    const params = new URLSearchParams();
    if (include.length > 0) {
      params.append("include", include.join(","));
    }

    const url =
      include.length > 0
        ? `/print-queues?${params.toString()}`
        : "/print-queues";
    const response = await api.get<ApiResponse<PrintQueueEntry[]>>(url);
    return response.data.data;
  }

  const config: UseQueryOptions<PrintQueueEntry[]> = {
    queryKey: [...KEYS.entries(), ...include.toSorted()],
    queryFn: getPrintQueueEntries,
  };

  return useQuery(config);
}

type AddPrintQueueEntryParams = {
  revisionId: Uuid;
  machineId?: Uuid | null;
};

type ApiErrorResponse = {
  message?: string;
};

export function useAddPrintQueueEntry() {
  const queryClient = useQueryClient();
  const addToast = useAddToast();

  return useMutation<
    PrintQueueEntry,
    AxiosError<ApiErrorResponse>,
    AddPrintQueueEntryParams
  >({
    mutationKey: [KEYS.entries(), "add"],
    mutationFn: async (params: AddPrintQueueEntryParams) => {
      const response = await api.post<ApiResponse<PrintQueueEntry>>(
        "/print-queues",
        params
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.entries() });
    },
    onError: (error) => {
      addToast({
        kind: "danger",
        title: t`components.queue.add-error-title`,
        content:
          error.response?.data?.message ?? t`components.queue.add-error-body`,
        timeout: 5_000,
      });
    },
  });
}

export function useDeletePrintQueueEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: Uuid) => {
      await api.delete<ApiResponse<void>>(`/print-queues/${entryId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.entries() });
    },
  });
}

type ReorderPrintQueueEntryParams = {
  entryId: Uuid;
  beforeId?: Uuid | null;
  afterId?: Uuid | null;
};

export function useReorderPrintQueueEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entryId,
      beforeId,
      afterId,
    }: ReorderPrintQueueEntryParams) => {
      const response = await api.post<ApiResponse<PrintQueueEntry>>(
        `/print-queues/${entryId}/reorder`,
        { beforeId, afterId }
      );
      return response.data.data;
    },

    onMutate: async (params) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: KEYS.entries() });

      // Snapshot the previous value
      const previousEntries = queryClient.getQueryData<PrintQueueEntry[]>(
        KEYS.entries()
      );

      if (!previousEntries) {
        return { previousEntries: undefined };
      }

      // Update local order immediately; server response is applied on refetch.
      const { entryId, beforeId, afterId } = params;
      const newEntries = [...previousEntries];

      // Remove the moving entry
      const movingIndex = newEntries.findIndex((e) => e.id === entryId);
      if (movingIndex === -1) return { previousEntries };

      const [movingEntry] = newEntries.splice(movingIndex, 1);

      // Find where to insert it
      let insertIndex: number;

      if (beforeId && afterId) {
        // Find the item that should come after us
        const afterIndex = newEntries.findIndex((e) => e.id === afterId);
        insertIndex = afterIndex !== -1 ? afterIndex : newEntries.length;
      } else if (afterId) {
        // Insert right before afterId
        const afterIndex = newEntries.findIndex((e) => e.id === afterId);
        insertIndex = afterIndex !== -1 ? afterIndex : 0;
      } else if (beforeId) {
        // Insert right after beforeId
        const beforeIndex = newEntries.findIndex((e) => e.id === beforeId);
        insertIndex = beforeIndex !== -1 ? beforeIndex + 1 : newEntries.length;
      } else {
        // If no bounds put at end
        insertIndex = newEntries.length;
      }

      newEntries.splice(insertIndex, 0, movingEntry);

      queryClient.setQueryData(KEYS.entries(), newEntries);

      return { previousEntries };
    },

    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousEntries) {
        queryClient.setQueryData(KEYS.entries(), context.previousEntries);
      }
    },

    onSettled: () => {
      // Always refetch to get the real positions from backend
      void queryClient.invalidateQueries({ queryKey: KEYS.entries() });
    },
  });
}

export function useRemovedPrintQueueEntries({
  limit,
  include = [],
}: {
  limit?: number;
  include?: ("users" | "projects" | "revisions")[];
} = {}) {
  async function getEntries(): Promise<PrintQueueEntry[]> {
    const params = new URLSearchParams({
      status: "removed",
    });

    if (limit) {
      params.append("limit", limit.toString());
    }

    if (include.length > 0) {
      params.append("include", include.join(","));
    }

    const response = await api.get<ApiResponse<PrintQueueEntry[]>>(
      `/print-queues?${params.toString()}`
    );

    return response.data.data;
  }

  return useQuery({
    queryKey: [...KEYS.removedEntries(), limit, ...include.toSorted()],
    queryFn: getEntries,
  });
}
